import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {setSuccessMessage} from '../../slices/messageSlice'
import {RootState} from '../../store'
import {
  ChangePasswordRequest,
  ChangePasswordResponse,
  EditUserProfileRequest,
  EditUserProfileResponse,
  GetUserProfileRequest,
  GetUserProfileResponse,
} from '../interfaces/profile'
import {getStorage} from '../../../helpers/utilities'
import {AuthModel} from '../../../interfaces/auth'
import {updateCurrentUser} from '../../slices/authSlice'

export const URL = `${process.env.REACT_APP_API_URL}/users/v1/`

export const profile = createApi({
  reducerPath: 'profile',
  baseQuery: fetchBaseQuery({
    baseUrl: URL,
    prepareHeaders: (headers, {getState}) => {
      const token = (getState() as RootState).authSlice.session_id
      headers.set('Authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['GetUserProfile'],
  endpoints: (builder) => ({
    getUserProfile: builder.query<GetUserProfileResponse, GetUserProfileRequest>({
      query: (data) => ({
        url: 'profile',
        method: 'Post',
        body: data,
      }),
      providesTags: ['GetUserProfile'],
    }),
    editUserProfile: builder.mutation<EditUserProfileResponse, EditUserProfileRequest>({
      query: (data) => ({
        url: 'profile/edit',
        method: 'Put',
        body: data,
      }),
      async onQueryStarted(arg, {dispatch, queryFulfilled}) {
        const authData = getStorage<AuthModel>(process.env.REACT_APP_AUTH_LOCAL_STORAGE_KEY || '')
        if (!authData) return

        const result = await queryFulfilled
        dispatch(
          updateCurrentUser({
            name: arg.requestContent.name,
            phoneNumber: arg.requestContent.phoneNumber,
          })
        )

        localStorage.setItem(
          process.env.REACT_APP_AUTH_LOCAL_STORAGE_KEY || '',
          JSON.stringify({
            enterprise: authData.enterprise,
            session_id: authData.session_id,
            user: {
              id: authData.user?.id,
              emailAddress: authData.user?.emailAddress,
              username: authData.user?.username,
              enterpriseId: authData.user?.enterpriseId,
              type: authData.user?.type,
              name: arg.requestContent.name,
              phoneNumber: arg.requestContent.phoneNumber,
            },
          })
        )
        dispatch(
          setSuccessMessage({
            title: result.data.info.title,
            description: result.data.info.message,
          })
        )
      },
      invalidatesTags: ['GetUserProfile'],
    }),
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
      query: (data) => ({
        url: `password/change`,
        method: 'PUT',
        body: data,
      }),
      async onQueryStarted(arg, {dispatch, queryFulfilled}) {
        const result = await queryFulfilled
        dispatch(
          setSuccessMessage({
            title: result.data.info.title,
            description: result.data.info.message,
          })
        )
      },
    }),
  }),
})

export const {useGetUserProfileQuery, useEditUserProfileMutation, useChangePasswordMutation} =
  profile
